(() => {
  const COLOR_OPTION_NAME = "Color";
  const NATURAL_VALUE = "Natural";
  const TINTED_VALUE = "Tinted";
  const CART_PROP_NAME = "Selected Color";

  let colorsCache = null;
  let selectedColorValue = "";

  // Track the last seen Color option so we can detect transitions
  let lastVariantColorValue = null;
  let initializedVariantWatch = false;

  function $all(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
  }

  function getActivePickerRoot() {
    return document.querySelector("[data-selected-color-picker]");
  }

  function findCurrentProductForm() {
    const forms = $all("form");
    return forms.find((f) => f.querySelector('input[name="id"]'));
  }

  function getCurrentColorOptionValue() {
    const form = findCurrentProductForm();
    if (form) {
      const checked = form.querySelector(
        `input[name="options[${CSS.escape(COLOR_OPTION_NAME)}]"]:checked`
      );
      if (checked) return (checked.value || "").trim();

      const select = form.querySelector(
        `select[name="options[${CSS.escape(COLOR_OPTION_NAME)}]"]`
      );
      if (select) return (select.value || "").trim();
    }

    // Nova fallback: read "Color: ___" label text if present
    const elements = Array.from(document.querySelectorAll("body *"));
    for (const el of elements) {
      const text = (el.textContent || "").trim();
      if (text.startsWith("Color:")) {
        const value = text.replace("Color:", "").trim();
        if (value === NATURAL_VALUE || value === TINTED_VALUE) return value;
      }
    }

    return "";
  }

  function setPickerVisible(visible) {
    const root = getActivePickerRoot();
    if (!root) return;

    const display = root.querySelector("[data-selected-color-display]");
    root.hidden = !visible;

    if (!visible) {
      selectedColorValue = "";
      if (display) {
        display.hidden = true;
        display.textContent = "";
      }
    } else {
      if (display) display.hidden = !selectedColorValue;
    }
  }

  async function loadColors() {
    if (colorsCache) return colorsCache;

    const root = getActivePickerRoot();
    const colorsUrl =
      root?.dataset?.colorsUrl ||
      root?.getAttribute("data-colors-url") ||
      "/assets/colors.json";

    const res = await fetch(colorsUrl, { cache: "no-store" });
    if (!res.ok) throw new Error("Failed to load assets/colors.json");

    const data = await res.json();
    const list = Array.isArray(data) ? data : data.colors || [];
    colorsCache = list;
    return colorsCache;
  }

  function getScrollContainer(root) {
    if (!root) return null;
    return root.querySelector("[data-selected-color-grid]");
  }

  function openModal() {
    const root = getActivePickerRoot();
    if (!root) return;

    const modal = root.querySelector("[data-selected-color-modal]");
    if (!modal) return;

    document.documentElement.classList.add("selected-color-picker--open");
    document.body.classList.add("selected-color-picker--open");

    if (typeof modal.showModal === "function") {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute("open", "");
    }

    // Trap wheel to the grid scroll container
    const scroller = getScrollContainer(root);
    if (scroller && !modal.__wheelTrapped) {
      modal.__wheelTrapped = true;
      modal.addEventListener(
        "wheel",
        (e) => {
          if (!modal.contains(e.target)) return;
          e.preventDefault();
          scroller.scrollTop += e.deltaY;
        },
        { passive: false }
      );
    }
  }

  function closeModal() {
    const root = getActivePickerRoot();
    if (!root) return;

    const modal = root.querySelector("[data-selected-color-modal]");
    if (!modal) return;

    if (typeof modal.close === "function") {
      if (modal.open) modal.close();
    } else {
      modal.removeAttribute("open");
    }

    document.documentElement.classList.remove("selected-color-picker--open");
    document.body.classList.remove("selected-color-picker--open");
  }

  function updateChosenDisplay() {
    const root = getActivePickerRoot();
    if (!root) return;

    const display = root.querySelector("[data-selected-color-display]");
    if (!display) return;

    if (!selectedColorValue) {
      display.hidden = true;
      display.textContent = "";
      return;
    }

    display.hidden = false;
    display.textContent = `Selected: ${selectedColorValue}`;
  }

  function ensureCartPropertyInput(form) {
    if (!form) return;

    let input = form.querySelector(
      `input[name="properties[${CSS.escape(CART_PROP_NAME)}]"]`
    );

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${CART_PROP_NAME}]`;
      form.appendChild(input);
    }

    input.value = selectedColorValue || "";
  }

  async function maybeAutoOpenOnTintedTransition() {
    const root = getActivePickerRoot();
    if (!root || root.hidden) return;

    const modal = root.querySelector("[data-selected-color-modal]");
    if (!modal) return;
    if (modal.open) return;

    try {
      const colors = await loadColors();
      renderGrid(colors);
      openModal();
    } catch (err) {
      console.error(err);
    }
  }

  function applyVisibilityFromVariant() {
    const v = getCurrentColorOptionValue();

    // First run: establish baseline so refresh doesn't auto-open
    if (!initializedVariantWatch) {
      initializedVariantWatch = true;
      lastVariantColorValue = v || "";
    }

    if (v === TINTED_VALUE) {
      setPickerVisible(true);

      const transitionedIntoTinted =
        lastVariantColorValue !== TINTED_VALUE && v === TINTED_VALUE;

      // ✅ Only auto-open when user transitions into tinted AND no chosen color yet
      if (transitionedIntoTinted && !selectedColorValue) {
        // Wait a tick so Nova finishes updating UI layout
        setTimeout(() => {
          maybeAutoOpenOnTintedTransition();
        }, 50);
      }
    } else {
      selectedColorValue = "";
      updateChosenDisplay();
      setPickerVisible(false);
    }

    lastVariantColorValue = v || "";
  }

  function renderGrid(colors) {
    const root = getActivePickerRoot();
    if (!root) return;

    const grid = root.querySelector("[data-selected-color-grid]");
    if (!grid) return;

    grid.innerHTML = "";

    colors.forEach((c) => {
      const name = (c.name || "").toString().trim();
      if (!name) return;

      const number = (c.number || "").toString().trim();
      let hex = (c.hex || "").toString().trim();
      if (hex && !hex.startsWith("#")) hex = `#${hex}`;

      const tile = document.createElement("button");
      tile.type = "button";
      tile.className = "selected-color-picker__tile";
      tile.setAttribute("data-color-name", name);
      tile.setAttribute("aria-pressed", "false");

      const swatch = document.createElement("div");
      swatch.className = "selected-color-picker__swatch";
      if (hex) swatch.style.backgroundColor = hex;

      // Texture overlay
      const texture = document.createElement("div");
      texture.className = "selected-color-picker__texture";
      swatch.appendChild(texture);

      // Badge for FIOCCO (VIO01)
      if (number === "VIO01") {
        const badge = document.createElement("div");
        badge.className = "selected-color-picker__badge";
        badge.textContent = "OUR WHITEST WHITE";
        swatch.appendChild(badge);
      }

      // Label overlay
      const overlay = document.createElement("div");
      overlay.className = "selected-color-picker__overlay";

      const codeEl = document.createElement("div");
      codeEl.className = "selected-color-picker__code";
      codeEl.textContent = number || "";

      const nameEl = document.createElement("div");
      nameEl.className = "selected-color-picker__name";
      nameEl.textContent = (name || "").toUpperCase();

      overlay.appendChild(codeEl);
      overlay.appendChild(nameEl);

      swatch.appendChild(overlay);
      tile.appendChild(swatch);

      tile.addEventListener("click", () => {
        selectedColorValue = number ? `${number} ${name}` : name;
        updateChosenDisplay();

        $all("[data-color-name]", root).forEach((b) => {
          b.setAttribute("aria-pressed", b === tile ? "true" : "false");
        });

        closeModal();
      });

      grid.appendChild(tile);
    });
  }

  // --------------------
  // Events
  // --------------------

  document.addEventListener("click", async (e) => {
    const root = getActivePickerRoot();
    if (!root) return;

    const openBtn = e.target.closest("[data-open-selected-color-modal]");
    if (openBtn) {
      applyVisibilityFromVariant();
      if (root.hidden) return;

      try {
        const colors = await loadColors();
        renderGrid(colors);
        openModal();
      } catch (err) {
        console.error(err);
      }
      return;
    }

    const closeBtn = e.target.closest("[data-close-selected-color-modal]");
    if (closeBtn) {
      closeModal();
      return;
    }
  });

  // Variant changes (works for radios/selects)
  document.addEventListener("change", (e) => {
    const form = findCurrentProductForm();
    if (!form) return;
    if (!form.contains(e.target)) return;
    applyVisibilityFromVariant();
  });

  // Search filter
  document.addEventListener("input", (e) => {
    const input = e.target;
    if (!input || !input.matches("[data-selected-color-search]")) return;

    const root = input.closest("[data-selected-color-picker]");
    if (!root) return;

    const grid = root.querySelector("[data-selected-color-grid]");
    if (!grid) return;

    const q = (input.value || "").trim().toLowerCase();
    const tiles = Array.from(grid.querySelectorAll(".selected-color-picker__tile"));

    tiles.forEach((tile) => {
      const text = (tile.textContent || "").toLowerCase();
      tile.hidden = q ? !text.includes(q) : false;
    });
  });

  // Cart property injection
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('input[name="id"]')) return;

      const v = getCurrentColorOptionValue();
      if (v !== TINTED_VALUE) return;

      ensureCartPropertyInput(form);
    },
    true
  );

  // ✅ Nova fails to emit "change" sometimes for pill UIs.
  // So we poll lightly to detect transition Natural -> Tinted reliably.
  let pollTimer = null;
  function startVariantPoll() {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      const root = getActivePickerRoot();
      if (!root) return;
      applyVisibilityFromVariant();
    }, 250);
  }

  document.addEventListener("DOMContentLoaded", () => {
    applyVisibilityFromVariant();
    startVariantPoll();
  });
})();